import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { Check, ChevronRight, Sparkles } from "lucide-react";
import { useSeo } from "@/lib/useSeo";
import { cycleLabel, gstNote, inr, mediaUrl, useApi } from "@/lib/api";
import { fbEvent } from "@/lib/consent";
import { useSite } from "@/context/SiteContext";
import { Badge, Btn, Reveal, Spinner } from "@/components/site/kit";
import { DynIcon } from "@/components/site/DynIcon";
import { LeadForm } from "@/components/site/LeadForm";
import { CheckoutDialog } from "@/components/site/CheckoutDialog";
import NotFound from "@/pages/NotFound";

export default function ProductDetail() {
  const { slug } = useParams();
  const { hash } = useLocation();
  const { settings } = useSite();
  const { data: p, isLoading, isError } = useApi(`/products/${slug}`);
  const [checkout, setCheckout] = useState(false);
  useSeo({ title: p?.seo_title || p?.title, description: p?.seo_description || p?.short_description, image: p?.image });

  useEffect(() => {
    if (p) fbEvent("ViewContent", { content_name: p.title, content_ids: [p.slug], content_type: "product", value: p.price || 0, currency: "INR" });
  }, [p]);
  useEffect(() => {
    if (p && hash === "#demo") setTimeout(() => document.getElementById("demo")?.scrollIntoView({ behavior: "smooth" }), 150);
  }, [p, hash]);

  if (isLoading) return <Spinner className="min-h-[60vh]" />;
  if (isError || !p) return <NotFound />;
  const item = p.price && p.buy_enabled !== false ? { type: "product", id: p.slug, name: p.title, price: p.price, billing_cycle: p.billing_cycle } : null;

  return (
    <>
      <section className="relative overflow-hidden border-b border-line bg-white">
        <div className="dot-grid absolute inset-0 opacity-50" aria-hidden="true" />
        <div className="container-x relative grid gap-12 py-14 lg:grid-cols-[1.2fr_0.8fr] lg:py-20">
          <Reveal>
            <nav className="mb-6 flex items-center gap-1 text-sm text-muted2" aria-label="Breadcrumb">
              <Link to="/products" className="hover:text-ocean" data-testid="breadcrumb-products">Products</Link><ChevronRight className="h-4 w-4" /><span className="text-navy">{p.title}</span>
            </nav>
            <Badge>{p.category}</Badge>
            <h1 className="mt-5 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl" data-testid="product-title">{p.title}</h1>
            <p className="mt-4 text-lg font-semibold text-ocean">{p.tagline}</p>
            <p className="mt-4 max-w-2xl leading-relaxed text-body" data-testid="product-description">{p.description}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              {item && <Btn onClick={() => setCheckout(true)} testId="product-buy-now-btn">Buy Now</Btn>}
              <Btn href={undefined} onClick={() => document.getElementById("demo")?.scrollIntoView({ behavior: "smooth" })} variant="secondary" testId="product-request-demo-btn">Request Demo</Btn>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="card-base p-7" data-testid="product-price-card">
              {p.image ? <img src={mediaUrl(p.image)} alt={p.title} loading="lazy" className="mb-6 w-full rounded-xl" /> : (
                <span className="grid h-16 w-16 place-items-center rounded-2xl bg-indigo-brand text-sky"><DynIcon name={p.icon} className="h-8 w-8" /></span>
              )}
              {item ? (
                <>
                  <p className="mt-6 text-sm font-semibold text-muted2">Starting at</p>
                  <p className="font-heading text-4xl font-extrabold text-navy" data-testid="product-price">{inr(p.price)}<span className="ml-1 text-base font-medium text-muted2">{cycleLabel(p.billing_cycle)}</span></p>
                  <p className="mt-1 text-xs text-muted2">{gstNote(settings)}</p>
                </>
              ) : <p className="mt-6 font-heading text-2xl font-bold">Custom pricing</p>}
              <ul className="mt-6 space-y-2.5 border-t border-line pt-6 text-sm">
                {["Free live demo before you buy", "Setup & onboarding included", "Support " + (settings?.support_hours || "")].map((t) => <li key={t} className="flex gap-2"><Check className="h-4 w-4 shrink-0 text-success" />{t}</li>)}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>
      <section className="section bg-page">
        <div className="container-x grid gap-12 lg:grid-cols-2">
          <Reveal>
            <h2 className="text-3xl font-extrabold">Key <span className="text-ocean">features</span></h2>
            <ul className="mt-8 grid gap-3" data-testid="product-features">
              {(p.features || []).map((f) => <li key={f} className="card-base flex gap-3 p-4 text-sm font-medium text-navy"><span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-[#DCFCE7]"><Check className="h-3.5 w-3.5 text-success" /></span>{f}</li>)}
            </ul>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="text-3xl font-extrabold">Business <span className="text-ocean">benefits</span></h2>
            <div className="indigo-gradient mt-8 rounded-2xl p-7" data-testid="product-benefits">
              {(p.benefits || []).map((b) => <p key={b} className="flex gap-3 border-b border-white/10 py-3.5 text-white last:border-0"><Sparkles className="h-5 w-5 shrink-0 text-cta" />{b}</p>)}
            </div>
          </Reveal>
        </div>
      </section>
      <section id="demo" className="section scroll-mt-20 bg-white">
        <div className="container-x grid gap-10 lg:grid-cols-[0.9fr_1.1fr]">
          <Reveal>
            <Badge>Free live demo</Badge>
            <h2 className="mt-5 text-3xl font-extrabold sm:text-4xl">See <span className="text-ocean">{p.title}</span> in action</h2>
            <p className="mt-4 text-body">Book a free 30-minute live demo with our product expert. We'll walk you through the features using your own business scenario. (We offer demos, not free trials.)</p>
          </Reveal>
          <div className="card-base p-6 sm:p-8"><LeadForm source="demo request" product={p.title} showService={false} submitLabel="Request Demo" testPrefix="demo-form" /></div>
        </div>
      </section>
      {item && <CheckoutDialog item={item} open={checkout} onOpenChange={setCheckout} />}
    </>
  );
}
